import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Stack,
  useTheme,
} from "@mui/material";
import OrbLoader from "../components/common/OrbLoader";
import { useGetPlatformMappingsQuery } from "../store/apis/briefingApi";
import { Hub } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { selectSidebarCollapsed } from "../store/slices/app/appSelector";
import PageBreadcrumbs from "../components/layout/PageBreadcrumbs";
import { resolveBriefingPlatformIcon } from "../utils/iconUtils";

const PlatformMappings: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const collapsed = useSelector(selectSidebarCollapsed);

  const { data, isLoading, isError } = useGetPlatformMappingsQuery();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          p: 4,
        }}
      >
        <OrbLoader
          size="large"
          label={t("common.loading", { defaultValue: "Cargando…" })}
        />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Alert severity="error">
          {t("common.errorLoading", "An error occurred while loading data.")}
        </Alert>
      </Box>
    );
  }

  const mappings = data?.data || [];

  return (
    <Box sx={{ display: "flex" }}>
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "80px" : "250px",
        }}
      >
        {/* This wrapper has no padding of its own, so the trail sets its own. */}
        <PageBreadcrumbs sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 } }} />

        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Hub sx={{ color: theme.palette.primary.main, fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {t("platformMappings.title", "Platform Mappings")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(
                  "platformMappings.subtitle",
                  "View the canonical platform to campaign type and format mappings used in assignments."
                )}
              </Typography>
            </Box>
          </Stack>

          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 3,
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
              border: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("platformMappings.platform", "Platform")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("platformMappings.realPlatform", "Real Platform")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("platformMappings.campaignTypes", "Campaign Types")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("platformMappings.defaultFormat", "Default Format")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("platformMappings.formatOverrides", "Format Overrides")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("platformMappings.adtechCostOverrides", "Adtech Cost")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("platformMappings.sourceIdLevel", "Source ID Level")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mappings.map((mapping) => (
                  <TableRow
                    key={mapping.platform}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box
                          component="img"
                          src={
                            resolveBriefingPlatformIcon(mapping.platform)
                              .iconPath
                          }
                          alt={mapping.platform}
                          sx={{ width: 30, height: 30 }}
                        />
                        <Typography variant="body2" fontWeight="bold">
                          {mapping.platform}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {mapping.realPlatform ? (
                        <Chip
                          label={mapping.realPlatform}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {mapping.campaignTypes.map((ct) => (
                          <Chip
                            key={ct}
                            label={ct}
                            size="small"
                            sx={{ backgroundColor: theme.palette.grey[100] }}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={mapping.defaultFormat}
                        size="small"
                        color="secondary"
                      />
                    </TableCell>
                    <TableCell>
                      {Object.keys(mapping.formatByType || {}).length > 0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          {Object.entries(mapping.formatByType || {}).map(
                            ([type, format]) => (
                              <Typography key={type} variant="caption">
                                <strong>{type}:</strong> {format}
                              </Typography>
                            )
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {t("platformMappings.noOverrides", "None")}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {Object.keys(mapping.adserverCostByType || {}).length >
                      0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          {Object.entries(mapping.adserverCostByType || {}).map(
                            ([type, cost]) => (
                              <Typography key={type} variant="caption">
                                <strong>{type}:</strong> €{String(cost)}
                              </Typography>
                            )
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {t("platformMappings.noOverrides", "None")}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={mapping.defaultCampaignLevel}
                        size="small"
                        color={
                          mapping.defaultCampaignLevel === "INSERTIONORDER"
                            ? "info"
                            : "default"
                        }
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {mappings.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        {t(
                          "platformMappings.noData",
                          "No mapping records found."
                        )}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>
  );
};

export default PlatformMappings;
